import { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Popover, Select, message } from "antd";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { IconArrowDown, IconCredit } from "@icons/index";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { CommonButton } from "@components/CommonButton";
import { BasicInfoContent } from "@pages/videoCreating/BasicInfo/BasicInfoContent";
import type { ProductImage } from "@pages/videoCreating/videoCreatingSlice";
import { fetchCreateOptions, selectVideoParams } from "@pages/videoCreating/videoCreatingSlice";
import { VideoParamsSelect } from "@pages/videoCreating/VideoParamsSelect";
import videoCreatingService, { VideoCreationRequest } from "@apis/videoCreatingService";
import { useConsumeCredit } from "@pages/videoCreating/hooks";
import { fetchAiCredits } from "@store/globalSlice";
import MemberPopoverContent from "@components/MemberPopoverContent";
import { shouldShowNotificationGuide } from "@utils/notification";
import { notificationModalAtom } from "@src/jotai/notification";
import { useSetAtom } from "jotai";
import { VideoGeneratingLoading } from "@pages/videoCreating/AutoDescription";
import { fetchVideoGenerateList } from "@pages/library/librarySlice";
import { useNeedAiAnalyzeImages } from "@pages/videoCreating/BasicInfo/useNeedAiAnalyzeImages";
import { AIAnalyzeButton } from "./AIAnalyzeButton";
import { sliceProductCategory, transformAbbreviationCountry2FullCountry } from "@utils/util";
import { SelectLanguage } from "@components/SelectLanguage/SelectLanguage";

interface VideoCreationModalProps {
  key: string;
  open: boolean;
  onClose: () => void;
  productItem?: {
    id?: string;
    coverImageUrl?: string;
    productTitle?: string;
    productCategory?: string;
    country?: string;
  };
  requestId?: string;
  scriptLanguage?: string;
  activeScript?: number;
  onParseProductFailed?: () => void;
}

export const VideoCreationModal = ({
  key,
  open,
  onClose,
  productItem,
  requestId,
  activeScript,
  scriptLanguage,
  onParseProductFailed,
}: VideoCreationModalProps) => {
  const dispatch = useAppDispatch();
  const { totalRemain } = useAppSelector((state) => state.globalReducer.creditInfo);
  const { sellingCountry: sellingCountryOptions } = useAppSelector((state) => state.configReducer);
  const { sellingPoints: sellingPointsData } = useAppSelector((state) => state.scriptRewriteReducer);
  const { aspectRatio, duration, quantity, resolution, internalModelId } = useAppSelector(selectVideoParams);
  const totalCost = useConsumeCredit(open);
  const setNotificationModal = useSetAtom(notificationModalAtom);

  const [images, setImages] = useState<ProductImage[]>([]);
  const [productTitle, setProductTitle] = useState(productItem?.productTitle || "");
  const [productCategory, setProductCategory] = useState(productItem?.productCategory || "");
  const [sellingCountry, setSellingCountry] = useState<string | undefined>(undefined);
  const fullSellingCountry = useMemo(() => transformAbbreviationCountry2FullCountry(sellingCountry), [sellingCountry]);
  const [language, setLanguage] = useState("en");
  const [loading, setLoading] = useState(false);
  const [countDown, setCountDown] = useState(60);
  const closedDuringLoadingRef = useRef(false);
  const needAiAnalyzeImages = useNeedAiAnalyzeImages(images);

  useEffect(() => {
    if (!loading) {
      setCountDown(60);
      return;
    }
    const timer = setInterval(() => {
      setCountDown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [loading]);

  useEffect(() => {
    if (open) {
      dispatch(fetchCreateOptions({ videoLanguage: language }));
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [open, language]);

  useEffect(() => {
    if (!open) return;
    closedDuringLoadingRef.current = false;
    setImages([]);
    setProductTitle("");
    setProductCategory("");
    setSellingCountry(undefined);
    setLanguage(scriptLanguage && scriptLanguage !== "auto" ? scriptLanguage : "en");

    if (!productItem) return;

    setProductTitle(productItem.productTitle || "");
    setProductCategory(productItem.productCategory || "");
    setSellingCountry(productItem.country || undefined);

    if (productItem.coverImageUrl) {
      setImages([
        { id: "cover", url: productItem.coverImageUrl, description: "", isChangedUrl: false, aiAnalyzed: false },
      ]);
    }
  }, [open]);

  const handleClose = () => {
    if (loading) {
      closedDuringLoadingRef.current = true;
    }
    onClose();
  };

  const canGenerate = useMemo(() => {
    if (totalRemain < totalCost) return false;
    if (images.length === 0) return false;
    if (!productTitle.trim() || !productCategory.trim()) return false;
    return true;
  }, [totalRemain, totalCost, images.length, productTitle]);

  const performGenerate = async () => {
    if (!canGenerate || loading) return;

    try {
      if (!internalModelId) {
        message.error(t("Please select a model"));
        return;
      }

      setLoading(true);
      closedDuringLoadingRef.current = false;

      const res: any = await dispatch(fetchAiCredits());
      const currentRemain = res.payload?.totalRemain || 0;

      if (currentRemain < totalCost) {
        message.error(t("Insufficient credits"));
        return;
      }

      const imageInfos = images
        .filter((img) => img.url)
        .map((img) => ({
          imageUrl: img.url,
          description: img.description || undefined,
          assetId: img.assetId || 0,
          needsFaceMasking: img.needsFaceMasking ?? false,
        }));

      if (imageInfos.length === 0) {
        message.error(t("Please upload at least one image"));
        return;
      }

      const sellingPoints = (sellingPointsData.data || [])
        .filter((sp) => sp.checked)
        .map((sp) => sp.keyWord)
        .filter((s): s is string => Boolean(s));

      const requestData: VideoCreationRequest = {
        imageInfos,
        aspectRatio,
        duration,
        quantity,
        resolution,
        productTitle,
        requestId,
        activeScript: typeof activeScript === "number" ? activeScript + 1 : undefined,
        sellingCountry: sellingCountry || "United States",
        language,
        categoryNames: sliceProductCategory(productCategory),
        sellingPoints: sellingPoints,
        productId: productItem?.id || "",
        country: productItem?.country || "",
        internalModelId,
      };

      await videoCreatingService.videoCreation(requestData);
      dispatch(fetchAiCredits());
      // 开启视频生成接口轮询
      dispatch(fetchVideoGenerateList({}));

      message.success({
        content: (
          <span>
            {t("Video generation started")}
            <a
              onClick={() => window.open("/library?type=video", "_blank")}
              style={{ marginLeft: 4, textDecoration: "underline", cursor: "pointer" }}
            >
              {t("library")}
            </a>
          </span>
        ),
        duration: 8,
      });

      onClose();
    } catch (error: any) {
      message.error(error?.message || t("Failed to generate video"));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = () => {
    if (!canGenerate || loading) return;
    if (shouldShowNotificationGuide()) {
      setNotificationModal({ open: true, onComplete: performGenerate });
      return;
    }
    performGenerate();
  };

  if (!open) return null;

  return (
    <Modal
      key={key}
      open={open}
      onCancel={handleClose}
      width={728}
      destroyOnHidden
      title={
        <span className="text-[18px] font-bold">
          <Trans>Confirm Basic Information</Trans>
        </span>
      }
      footer={
        !loading && (
          <div className="flex items-center justify-between gap-[8px] pt-[16px] border-t border-kds-border">
            <VideoParamsSelect showResolution />

            <div className="flex-1" />

            {totalRemain < totalCost ? (
              <Popover content={<MemberPopoverContent remainCredit={totalRemain} action="Credits" />} trigger="hover">
                <CommonButton type="primary" className="h-[40px] px-[24px] min-w-[140px]" disabled>
                  <Trans>Generate</Trans>
                  <span className="flex items-center gap-1 ml-2">
                    <IconCredit width={16} height={16} />
                    <span className="font-semibold">{totalCost}</span>
                  </span>
                </CommonButton>
              </Popover>
            ) : needAiAnalyzeImages.length > 0 ? (
              <AIAnalyzeButton
                images={images}
                productTitle={productTitle}
                productCategory={productCategory}
                onSuccess={(aiAnalyzedImages) => {
                  setImages(aiAnalyzedImages);
                }}
              />
            ) : (
              <CommonButton
                type="primary"
                className="h-[40px] px-[24px] min-w-[140px]"
                disabled={!canGenerate || loading}
                loading={loading}
                onClick={handleGenerate}
              >
                <Trans>Generate</Trans>
                <span className="flex items-center gap-1 ml-2">
                  <IconCredit width={16} height={16} />
                  <span className="font-semibold">{totalCost}</span>
                </span>
              </CommonButton>
            )}
          </div>
        )
      }
    >
      <div className="flex flex-col h-[65vh] overflow-y-auto -mr-[20px]">
        {loading ? (
          <VideoGeneratingLoading countDown={countDown} />
        ) : (
          <div className="w-[680px]">
            <BasicInfoContent
              images={images}
              onImagesChange={setImages}
              productTitle={productTitle}
              productId={productItem?.id}
              country={productItem?.country}
              productCategory={productCategory}
              onChangeBasicInfo={(e) => {
                if (e.productTitle) setProductTitle(e.productTitle);
                if (e.productCategory) setProductCategory(e.productCategory);
              }}
              onParseProductFailed={onParseProductFailed}
            />

            {/* Target Market & Language */}
            <section className="flex gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-1 mb-[4px]">
                  <span className="text-kds-fg-soft font-semibold text-[16px]">
                    <Trans>Target Market</Trans>
                  </span>
                </div>
                <Select
                  value={fullSellingCountry}
                  onChange={setSellingCountry}
                  placeholder={t("Select the region you are selling to...")}
                  className="w-full h-[44px]"
                  options={sellingCountryOptions.map((r) => ({ label: t(r.label), value: r.name }))}
                  suffixIcon={<IconArrowDown className="w-[16px] h-[16px] text-kds-fg-subtle" />}
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-1 mb-[4px]">
                  <span className="text-kds-fg-soft font-semibold text-[16px]">
                    <Trans>Language</Trans>
                  </span>
                </div>
                <SelectLanguage value={language} onChange={setLanguage} className="h-[44px]" />
              </div>
            </section>
          </div>
        )}
      </div>
    </Modal>
  );
};
